import { useEffect, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Custom hook for debounced search functionality
 *
 * This hook provides debounced search state management with the following improvements:
 *
 * 1. **Initial State Synchronization**: If an initial search term is provided,
 *    both searchTerm and debouncedSearchTerm are initialized with the same value,
 *    ensuring immediate synchronization without waiting for debounce delay.
 *
 * 2. **Empty String Optimization**: Empty strings and whitespace-only strings
 *    are immediately reflected in debouncedSearchTerm without debouncing,
 *    preventing unnecessary API calls for empty searches.
 *
 * 3. **Proper Cleanup**: Timeouts are properly cleared to prevent memory leaks
 *    and ensure only the latest search term triggers API calls.
 *
 * @param initialSearchTerm - Optional initial search term (defaults to empty string)
 * @returns Object containing searchTerm, debouncedSearchTerm, and setSearchTerm
 *
 * @example
 * ```tsx
 * const { searchTerm, debouncedSearchTerm, setSearchTerm } = useEntitySearch();
 *
 * // With initial value
 * const { searchTerm, debouncedSearchTerm, setSearchTerm } = useEntitySearch('initial');
 * ```
 */
export const useEntitySearch = (initialSearchTerm = '') => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearchTerm);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search effect
  useEffect(() => {
    // Skip debouncing for empty strings to avoid unnecessary API calls
    if (searchTerm.trim() === '') {
      setDebouncedSearchTerm('');
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  return {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm
  };
};
